import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import type { ReportField, ChartType, DateGrouping } from './types'
import { DATE_GROUPINGS } from './types'
import { BarChart3, PieChart, LineChart as LineChartIcon, Table2, TrendingUp } from 'lucide-react'

interface GroupingConfigProps {
  dataSource: string
  fields: ReportField[]
  groupBy?: string
  dateGrouping?: DateGrouping
  sortBy?: string
  sortOrder: 'asc' | 'desc'
  chartType: ChartType
  limit: number
  hasAggregations: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onGroupByChange: (groupBy: string | undefined) => void
  onDateGroupingChange: (dateGrouping: DateGrouping | undefined) => void
  onSortByChange: (sortBy: string | undefined) => void
  onSortOrderChange: (order: 'asc' | 'desc') => void
  onChartTypeChange: (chartType: ChartType) => void
  onLimitChange: (limit: number) => void
}

const CHART_OPTIONS: { value: ChartType; label: string; icon: typeof BarChart3 }[] = [
  { value: 'table', label: 'Table', icon: Table2 },
  { value: 'bar', label: 'Bar Chart', icon: BarChart3 },
  { value: 'line', label: 'Line Chart', icon: LineChartIcon },
  { value: 'area', label: 'Area Chart', icon: TrendingUp },
  { value: 'pie', label: 'Pie Chart', icon: PieChart },
]

export function GroupingConfig({
  dataSource,
  fields,
  groupBy,
  dateGrouping,
  sortBy,
  sortOrder,
  chartType,
  limit,
  hasAggregations,
  open,
  onOpenChange,
  onGroupByChange,
  onDateGroupingChange,
  onSortByChange,
  onSortOrderChange,
  onChartTypeChange,
  onLimitChange,
}: GroupingConfigProps) {
  const selectedFields = fields.filter((f) => f.selected)
  const nonAggFields = selectedFields.filter((f) => f.aggregation === 'none' || !f.aggregation)
  const groupByField = fields.find((f) => f.name === groupBy)
  const isDateGroupBy = groupByField?.type === 'date'

  return (
    <Card>
      <Collapsible open={open} onOpenChange={onOpenChange}>
        <CollapsibleTrigger className="w-full" disabled={!dataSource}>
          <CardHeader
            className={cn(
              'flex flex-row items-center justify-between p-4 cursor-pointer rounded-t-lg',
              dataSource ? 'hover:bg-muted/50' : 'opacity-50 cursor-not-allowed'
            )}
          >
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">Visualization</CardTitle>
              {chartType && (
                <Badge variant="secondary" className="text-xs capitalize">
                  {chartType}
                </Badge>
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            {/* Chart Type */}
            <div>
              <Label>Chart Type</Label>
              <div
                className="grid gap-3 md:grid-cols-5 mt-2"
                role="radiogroup"
                aria-label="Chart type"
              >
                {CHART_OPTIONS.map(({ value, label, icon: Icon }) => (
                  <div
                    key={value}
                    className={`p-4 border rounded-lg cursor-pointer text-center transition-colors ${
                      chartType === value ? 'bg-primary/10 border-primary' : 'hover:bg-muted'
                    }`}
                    onClick={() => onChartTypeChange(value)}
                    role="radio"
                    aria-checked={chartType === value}
                    aria-label={`Select ${label}`}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onChartTypeChange(value)
                      }
                    }}
                  >
                    <Icon className="h-8 w-8 mx-auto mb-2" aria-hidden="true" />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Group By + Date Grouping */}
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="group-by">
                  Group By {hasAggregations && <span className="text-[#B45309]">*</span>}
                </Label>
                <Select
                  value={groupBy || '__none__'}
                  onValueChange={(value) =>
                    onGroupByChange(value === '__none__' ? undefined : value)
                  }
                >
                  <SelectTrigger id="group-by">
                    <SelectValue placeholder="Select grouping field" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {nonAggFields.map((field) => (
                      <SelectItem key={field.id} value={field.name}>
                        {field.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {hasAggregations && !groupBy && (
                  <p className="text-xs text-[#B45309] mt-1">
                    Recommended: Select a field to group results when using aggregations
                  </p>
                )}
              </div>

              {/* Date Grouping - only shown when grouping by a date field */}
              {isDateGroupBy && (
                <div>
                  <Label htmlFor="date-grouping">Date Granularity</Label>
                  <Select
                    value={dateGrouping || '__none__'}
                    onValueChange={(value) =>
                      onDateGroupingChange(
                        (value === '__none__' ? undefined : value) as DateGrouping | undefined
                      )
                    }
                  >
                    <SelectTrigger id="date-grouping">
                      <SelectValue placeholder="Select granularity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Raw dates</SelectItem>
                      {DATE_GROUPINGS.map((dg) => (
                        <SelectItem key={dg.value} value={dg.value}>
                          {dg.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Group dates by time period for trend analysis
                  </p>
                </div>
              )}
            </div>

            {/* Sort + Limit */}
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label htmlFor="sort-by">Sort By</Label>
                <Select
                  value={sortBy || '__none__'}
                  onValueChange={(value) =>
                    onSortByChange(value === '__none__' ? undefined : value)
                  }
                >
                  <SelectTrigger id="sort-by">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {selectedFields.map((field) => (
                      <SelectItem key={field.id} value={field.name}>
                        {field.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="sort-order">Sort Order</Label>
                <Select
                  value={sortOrder}
                  onValueChange={(value) => onSortOrderChange(value as 'asc' | 'desc')}
                >
                  <SelectTrigger id="sort-order">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="asc">Ascending</SelectItem>
                    <SelectItem value="desc">Descending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="limit">Result Limit</Label>
                <Input
                  id="limit"
                  type="number"
                  min="1"
                  max="1000"
                  value={limit}
                  onChange={(e) => onLimitChange(parseInt(e.target.value) || 100)}
                />
              </div>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}
